import XLSX from 'xlsx';
import { ClassMap, scssSafeValue } from './class-map';
import { ClassDefinition } from '../components/ClassEditor';

export interface ReportPage {
  classes: string[];
}

export interface ReportSection {
  name: string;
  pages: ReportPage[];
}

export interface ReportTable {
  name: string;
  classes: string[];
}

export interface CssReportInput {
  sections: ReportSection[];
  tables: ReportTable[];
  classMap: ClassMap;
  pageOffset?: number;
  showFrom?: number;
}

export const parseClassAttribute = (value?: string | null): string[] =>
  (value ?? '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);

export function classCssText(def: ClassDefinition): string {
  const lines: string[] = [];

  Object.entries(def.properties ?? {}).forEach(([prop, value]) => {
    if (!value?.trim()) return;
    const safe = scssSafeValue(value);
    if (!safe) return;
    lines.push(`${prop}: ${prop === 'font-family' ? `"${safe}"` : safe};`);
  });

  const bold = scssSafeValue(def.boldFontFamily ?? '');
  if (bold) {
    lines.push(`.xrp-body-bold, .xrp-heading-bold, strong, b { font-family: "${bold}"; }`);
  }
  const italic = scssSafeValue(def.italicFontFamily ?? '');
  if (italic) {
    lines.push(`.xrp-body-italic, .xrp-heading-italic, em, i { font-family: "${italic}"; }`);
  }

  if (def.customCss?.trim()) {
    lines.push(def.customCss.trim());
  }

  return lines.join('\n');
}

export const PAGE_SHEET_HEADER = ['Sektion', 'Sida', 'Klass'];
export const TABLE_SHEET_HEADER = ['Tabellnamn', 'Klass'];
export const CLASS_SHEET_HEADER = ['Klass', 'Etikett SV', 'Etikett EN', 'Element', 'Scope', 'Kommentar', 'CSS'];

// `Sida` is the document page number the filer sees: a running index across every
// section plus the document's pageNumbering offset, not a per-section index.
// Pages before `showFrom` carry no number in the document (Area.tsx), and a computed
// 0 renders blank there too, so the report leaves those cells empty rather than
// printing a number nobody sees.
export function pageSheetRows(sections: ReportSection[], pageOffset = 0, showFrom = 0): string[][] {
  const rows: string[][] = [PAGE_SHEET_HEADER];
  let physicalPage = 0;

  sections.forEach((section) => {
    section.pages.forEach((page) => {
      physicalPage += 1;
      const documentPage = physicalPage + pageOffset;
      const pageLabel = physicalPage < showFrom || documentPage === 0 ? '' : String(documentPage);
      if (page.classes.length === 0) {
        rows.push([section.name, pageLabel, '']);
        return;
      }
      page.classes.forEach((className) => {
        rows.push([section.name, pageLabel, className]);
      });
    });
  });

  return rows;
}

export function tableSheetRows(tables: ReportTable[]): string[][] {
  const rows: string[][] = [TABLE_SHEET_HEADER];

  tables.forEach((table) => {
    if (table.classes.length === 0) {
      rows.push([table.name, '']);
      return;
    }
    table.classes.forEach((className) => {
      rows.push([table.name, className]);
    });
  });

  return rows;
}

export function classSheetRows(classMap: ClassMap): string[][] {
  const rows: string[][] = [CLASS_SHEET_HEADER];

  Object.entries(classMap)
    .sort(([a], [b]) => a.localeCompare(b, 'sv'))
    .forEach(([className, def]) => {
      rows.push([
        className,
        def.labelSv ?? '',
        def.labelEn ?? '',
        (def.elements ?? []).join(', '),
        (def.scopes ?? []).join(', '),
        def.comment ?? '',
        classCssText(def),
      ]);
    });

  return rows;
}

const columnWidths = (rows: string[][]): Array<{ wch: number }> => {
  const widths: number[] = [];
  rows.forEach((row) => {
    row.forEach((cell, index) => {
      const longestLine = String(cell ?? '')
        .split('\n')
        .reduce((max, line) => Math.max(max, line.length), 0);
      widths[index] = Math.max(widths[index] ?? 0, longestLine);
    });
  });
  return widths.map((width) => ({ wch: Math.min(Math.max(width + 2, 12), 60) }));
};

const addSheet = (workbook: XLSX.WorkBook, name: string, rows: string[][]) => {
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = columnWidths(rows);
  if (rows.length > 1) {
    sheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length - 1, c: rows[0].length - 1 } }) };
  }
  XLSX.utils.book_append_sheet(workbook, sheet, name);
};

export function buildCssReportWorkbook({ sections, tables, classMap, pageOffset = 0, showFrom = 0 }: CssReportInput): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new();
  addSheet(workbook, 'Sidblad', pageSheetRows(sections, pageOffset, showFrom));
  addSheet(workbook, 'Tabellblad', tableSheetRows(tables));
  addSheet(workbook, 'Klasser', classSheetRows(classMap));
  return workbook;
}

export const cssReportFileName = (documentName?: string): string => {
  const date = new Date().toISOString().slice(0, 10);
  const base = (documentName ?? '').trim().replace(/[\\/:*?"<>|]/g, '-');
  return base ? `${base}_klasser_${date}.xlsx` : `klasser_${date}.xlsx`;
};
